import { EMBA_API_HOST } from '@/constant';
import {
  type ApiError,
  ApiErrorType,
} from '@/errorHandling/context/ApiErrorContext';
import {
  CSRF_HEADER_NAME,
  ensureCsrfToken,
  refreshCsrfToken,
} from '@/utils/csrfToken';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  type ServerError,
  createHttpLink,
  fromPromise,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { GraphQLError } from 'graphql';

// unlimited-frontと同様にstatusCodeが503であればメンテナンスと判定にしています。
const isMaintenanceMode = (error: ServerError) => {
  return error.statusCode === 503;
};

export const createApolloClient = (setAPIError: (error: ApiError) => void) => {
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      for (const graphQLError of graphQLErrors) {
        const { message, extensions } = graphQLError;
        const errorCode = (extensions?.code ||
          'UNKNOWN') as ApiError['errorType'];

        console.log(`[GraphQL error]: Message: ${message}, Code: ${errorCode}`);

        if (ApiErrorType.includes(errorCode)) {
          setAPIError({
            errorType: errorCode as ApiError['errorType'],
            message,
          });
        } else {
          setAPIError({ errorType: 'UNKNOWN', message });
        }

        switch (errorCode) {
          case 'AGREEMENT_REQUIRED':
          case 'USER_ENROLLMENT_NOT_SELECTED':
          case 'UNAUTHORIZED':
            // 上記エラーの場合はSentry通知無し
            break;
          default:
            Sentry.withScope((scope) => {
              scope.setFingerprint([
                '{{ default }}',
                operation.operationName,
                errorCode as ApiError['errorType'],
              ]);
              Sentry.captureException(
                new GraphQLError(
                  `[GraphQL error]: Message: ${message}, Code: ${errorCode}`,
                  {
                    extensions: {
                      code: errorCode,
                    },
                  },
                ),
              );
            });
            break;
        }
      }
    }

    if (!networkError) return;

    // メンテナンスの時
    if (networkError.name === 'ServerError') {
      const serverError = networkError as ServerError;
      if (isMaintenanceMode(serverError)) {
        // メンテナンスモードのため、通知無しでエラー表示
        // serverError.resultは以下のjsonが返ってくる
        // https://github.com/globis-org/hodai/blob/master/public/emba_maintenance.json
        setAPIError({
          errorType: 'MAINTENANCE',
          message: (serverError.result as { message: string }).message,
        });

        return;
      }
    }

    // メンテナンス以外の時
    setAPIError({
      errorType: 'NETWORK_ERROR',
      message: networkError.message,
    });
  });

  // onErrorによるリトライは1回のみ。リトライ後も同じエラーが返った場合、再度onErrorは発火せずそのままエラーが伝播する。
  const csrfRetryLink = onError(({ graphQLErrors, operation, forward }) => {
    const csrfTokenInvalidError = graphQLErrors?.some(
      (e) =>
        e.extensions?.code ===
        ('CSRF_TOKEN_INVALID' satisfies ApiError['errorType']),
    );
    if (!csrfTokenInvalidError) return;

    return fromPromise(refreshCsrfToken()).flatMap(() => forward(operation));
  });

  const csrfLink = new ApolloLink((operation, forward) => {
    return fromPromise(ensureCsrfToken()).flatMap((csrfToken) => {
      operation.setContext(
        ({ headers = {} }: { headers: Record<string, string> }) => ({
          headers: {
            ...headers,
            [CSRF_HEADER_NAME]: csrfToken,
          },
        }),
      );
      return forward(operation);
    });
  });

  const operationNameLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-APOLLO-OPERATION-NAME': operation.operationName,
        // 'X-RELEASE-VERSION': process.env.npm_package_version,
      },
    }));

    return forward(operation);
  });

  const createApolloLink = () =>
    ApolloLink.from([
      errorLink,
      csrfRetryLink, // リトライ時は下流の csrfLink を再経由するため、新しいCSRFトークンをセットした状態でリクエストが飛ぶ
      operationNameLink,
      csrfLink,
      createHttpLink({
        uri: `${EMBA_API_HOST}/emba/graphql`,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      }),
    ]);

  const link = createApolloLink();

  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
      },
    },
  });
};
